/**
 * PcRightPseudoBox Component
 *
 * PC skin right pseudo box with window-frame style, sky-blue surface,
 * green football pitch decoration, and blue color accent (blue/red players).
 * Displays player username with truncation.
 */

import { memo } from 'react';
import type { PseudoBoxProps } from '../basics/constants/types';
import { buildClassNames } from '@/utils/classNames';
import { formatPseudoForDisplay, PSEUDO_BOX_CONFIG } from '../basics/utils/utils';
import { PC_PSEUDO_VIEWBOX } from './constants';

const PcRightPseudoBox = memo(function PcRightPseudoBox({
  pseudo,
  viewBox = PC_PSEUDO_VIEWBOX,
  className = ''
}: PseudoBoxProps) {
  const svgClassName = buildClassNames(className);
  const displayText = formatPseudoForDisplay(pseudo);

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Player username display box - PC right position"
      focusable="false"
    >
      <defs>
        {/* Blue color accent gradient */}
        <linearGradient
          id="pcPseudo_right_accent"
          x1="4.63"
          y1="62.4"
          x2="301.84"
          y2="62.4"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#555eff" />
          <stop offset="0.27" stopColor="#525cfb" />
          <stop offset="0.49" stopColor="#4c57f2" />
          <stop offset="0.68" stopColor="#414fe1" />
          <stop offset="0.86" stopColor="#3243ca" />
          <stop offset="1" stopColor="#2237b2" />
        </linearGradient>
        {/* Sky-blue surface gradient */}
        <linearGradient
          id="pcPseudo_right_surface"
          x1="300.36"
          y1="42.76"
          x2="7.11"
          y2="42.76"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#72bbfc" />
          <stop offset="0.48" stopColor="#a7dffc" />
          <stop offset="1" stopColor="#e6fbff" />
        </linearGradient>
        {/* Green football pitch decoration gradient */}
        <linearGradient
          id="pcPseudo_right_pitch"
          x1="297.68"
          y1="62.4"
          x2="12.16"
          y2="62.4"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#006837" />
          <stop offset="0.32" stopColor="#007039" />
          <stop offset="0.82" stopColor="#008841" />
          <stop offset="1" stopColor="#009245" />
        </linearGradient>
        {/* Text clip path */}
        <clipPath id="pcPseudo_right_textClip">
          <rect x="15" y="5" width="277" height="75" rx="20" />
        </clipPath>
      </defs>

      {/* Layer 1: Dark bottom base / shadow */}
      <path
        d="M4.63,49.73v25.43c0,7.44,3.98,14.39,10.54,17.91.05.03.1.06.16.08,7.6,4.02,14.77,3.09,23.44,2.69,10.56-.49,21.13,1.06,31.7,1.06,21.07,0,42.14.03,63.21,0,15.15-.02,30.07,0,40.73,0,16.34.01,24.51.02,25.36.04,35.9.56,38.76,2.99,51.49,0,8.8-2.07,11.95-4.29,20.17-4.17,7.79.11,12.78,2.21,19.21-.38,5.26-2.12,9.03-7.11,9.03-7.11.01-.01.02-.02.03-.04,1.53-1.77,2.33-4.06,2.31-6.4-.11-11.05-.21-22.1-.32-33.14-47.48,7.13-102.61,12.24-163.96,12.2-48.78-.03-93.42-3.3-133.12-8.17Z"
        fill="#383838"
      />

      {/* Layer 2: Blue color accent */}
      <path
        d="M4.63,40.08v33.56c2.04,3.02,5.42,7.08,10.7,9.86,7.6,4.02,14.77,3.09,23.44,2.69,10.56-.49,21.13,1.06,31.7,1.06,21.07,0,42.14.03,63.21,0,15.15-.02,30.07,0,40.73,0,16.34.01,24.51.02,25.36.04,35.9.56,38.76,2.99,51.49,0,8.8-2.07,11.95-4.29,20.17-4.17,7.79.11,20.04,2.21,26.47-.38,5.26-2.12,1.77-7.11,1.77-7.11,1.06-1.22,1.62-2.11,2.15-2.91-.12-12.15-.01-24.52-.13-36.67-47.48,7.13-102.61,12.24-163.96,12.2-48.78-.03-93.42-3.3-133.12-8.17Z"
        fill="url(#pcPseudo_right_accent)"
      />

      {/* Layer 3: Sky-blue surface */}
      <path
        d="M301.86,12.34v60.84c0,4.09-3.31,7.4-7.4,7.4H23.25c-10.28,0-18.62-8.34-18.62-18.62V12.34c0-4.09,3.31-7.4,7.4-7.4h282.43c4.09,0,7.4,3.31,7.4,7.4Z"
        fill="url(#pcPseudo_right_surface)"
      />

      {/* Layer 4: Green football pitch decoration */}
      <path
        d="M11.96,60.38c4.78-5.11,5.59,2.02,20.34,0,12.91-1.77,31.09,1.16,81.92,0,63.24-1.45,116.89-11.74,116.89-11.74,0,0,34.9-6.69,56.86,5.87,2.96,1.69,11.81,6.76,11.14,11.91-.25,1.9-1.7,3.16-2.22,3.61-14.98,12.99-140.22,3.95-140.22,3.95-68.44,1.78-90.51-3.66-119.33,2.22-5.04,1.03-19.93,4.41-25.38-2.51-2.9-3.68-2.99-10.12,0-13.31Z"
        fill="url(#pcPseudo_right_pitch)"
      />

      {/* Layer 5: Window frame */}
      <path
        d="M301.86,14.8v-4.77c0-2.81-2.28-5.09-5.09-5.09H9.72c-2.81,0-5.09,2.28-5.09,5.09v4.77h0v55.92h0v4.77c0,2.81,2.28,5.09,5.09,5.09h287.05c2.81,0,5.09-2.28,5.09-5.09v-4.77h-.04V14.8h.04ZM286.87,70.72H19.59c-2.81,0-5.09-2.28-5.09-5.09V19.89c0-2.81,2.28-5.09,5.09-5.09h267.28c2.81,0,5.09,2.28,5.09,5.09v45.73c0,2.81-2.28,5.09-5.09,5.09Z"
        fill="#f2f2f2"
      />

      {/* Dynamic player pseudo text */}
      <g clipPath="url(#pcPseudo_right_textClip)">
        <text
          x={PSEUDO_BOX_CONFIG.TEXT.RIGHT_X_POSITION}
          y={PSEUDO_BOX_CONFIG.TEXT.Y_POSITION}
          fill="#FFFFFF"
          fontSize={PSEUDO_BOX_CONFIG.TEXT.FONT_SIZE}
          fontWeight="bold"
          textAnchor="end"
          dominantBaseline="middle"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {displayText}
        </text>
      </g>
    </svg>
  );
});

export default PcRightPseudoBox;
